import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PaymentService } from '../services/payment.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import Paystack from '@paystack/inline-js';

@Component({
  selector: 'app-renewal-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './renewal-modal.component.html',
  styleUrls: ['./renewal-modal.component.css']
})
export class RenewalModalComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() visible: boolean = true;
  @Input() userEmail: string = ''; // Will be passed from parent component
  @Input() userPassword: string = ''; // Will be passed from parent component
  @Input() subscriptionStatus: string = ''; // Current subscription status for context-aware messaging
  @Output() renewalSuccess = new EventEmitter<void>();
  @ViewChild('paystackButton') paystackButton!: ElementRef;

  errorMessage: string | null = null;
  isLoading: boolean = false;
  plans: any[] = [];
  selectedPlan: any = null;
  private http: HttpClient;

  showUpgradeWarning: boolean = false;
  pendingPlanSelection: any = null;

  constructor(
    private authService: AuthService,
    private paymentService: PaymentService,
    private httpClient: HttpClient,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.http = httpClient;
    // Try to get email and password from router state if not provided as input
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state as { email: string, password?: string, subscriptionCode?: string, emailToken?: string };

    if (state) {
      if (state.email) this.userEmail = state.email;
      if (state.password) this.userPassword = state.password;
      if (state.subscriptionCode) this.subscriptionCode = state.subscriptionCode;
      if (state.emailToken) this.emailToken = state.emailToken;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
  }

  ngAfterViewInit(): void {
    this.fetchPlans();
  }

  ngOnDestroy(): void {
    // Clean up any Paystack references if needed
  }

  /**
   * Fetches available Paystack plans
   */
  async fetchPlans(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.plans = [];

    try {
      const apiUrl = `${environment.apiUrl}/api/Subscription/paystack/plans`;
      const response = await firstValueFrom(
        this.http.get(apiUrl, {
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Accept': 'application/json'
          },
          observe: 'response'
        })
      ) as any;

      if (response?.body?.status && Array.isArray(response.body.data)) {
        this.plans = response.body.data.map((plan: any) => ({
          id: plan.plan_code,
          planCode: plan.plan_code,
          name: plan.name,
          description: plan.description || 'No description available',
          amount: plan.amount,
          currency: plan.currency,
          interval: plan.interval,
          intervalCount: 1,
          features: [
            plan.description || 'Standard features included',
            // Add other features as needed
          ]
        }));

        // Automatically select a plan if needed
        if (this.plans.length > 0) {
          this.selectedPlan = this.plans[0];
        }
      }
    } catch (error) {
      //console.error('Error fetching plans:', error);
      this.errorMessage = 'Failed to load subscription plans. Please try again.';
    } finally {
      this.isLoading = false;
      // Add this line to trigger change detection after the async operation
      this.cdr.detectChanges();
    }
  }

  get modalHeading(): string {
    if (this.subscriptionStatus === 'plan_expired') return 'Promotional Plan Ended';
    if (this.subscriptionStatus === 'conversion_failed') return 'Subscription Setup Failed';
    return 'Subscription Renewal Required';
  }

  get modalDescription(): string {
    if (this.subscriptionStatus === 'plan_expired')
      return 'Your promotional plan has ended. Please select a new plan to continue. Your first payment will only be taken at the start of your next billing cycle.';
    if (this.subscriptionStatus === 'conversion_failed')
      return 'There was an issue activating your subscription. Please select a plan and try again.';
    return 'Your subscription is inactive or expired. Please select a plan to continue using the application.';
  }

  get isPlanExpired(): boolean {
    return this.subscriptionStatus === 'plan_expired';
  }

  onPlanSelect(plan: any): void {
    const currentAccountType = this.authService.getAccountType();
    const isCurrentlyCalculator = currentAccountType === 'calculator';
    const newPlanIsCalculator = (plan.name || '').toLowerCase().includes('calculator');

    // Warn if switching from calculator-only to a full plan
    if (isCurrentlyCalculator && !newPlanIsCalculator) {
      this.pendingPlanSelection = plan;
      this.showUpgradeWarning = true;
      return;
    }

    this.selectedPlan = plan;
  }

  confirmUpgrade(): void {
    this.selectedPlan = this.pendingPlanSelection;
    this.pendingPlanSelection = null;
    this.showUpgradeWarning = false;
  }

  cancelUpgrade(): void {
    this.pendingPlanSelection = null;
    this.showUpgradeWarning = false;
  }

  // Paystack subscription code and email token
  private subscriptionCode: string = '';
  private emailToken: string = '';

  /**
   * Initialize Paystack payment
   */
  async initializePaystack(): Promise<void> {
    if (!this.userEmail) {
      this.errorMessage = 'Email address is required for subscription renewal.';
      return;
    }

    if (!this.selectedPlan) {
      this.errorMessage = 'Please select a subscription plan.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    try {
      // Get Paystack instance from the payment service
      const paystack = this.paymentService.getPaystackInstance();

      paystack.newTransaction({
        key: environment.paystackPublicKey,
        email: this.userEmail,
        amount: this.selectedPlan.amount * 100, // Convert to kobo
        planCode: this.selectedPlan.planCode,
        onSuccess: async (transaction: any) => {
          this.isLoading = true;
          this.errorMessage = 'Processing your subscription...';

          try {
            // Single backend call that handles everything:
            // 1. Finds the new subscription on Paystack by comparing with the old one
            // 2. Cancels the old Paystack subscription
            // 3. Updates the local DB with the new subscription code
            const result = await this.http.post<any>(
              `${environment.apiUrl}/api/Subscription/switch-plan`,
              {
                email: this.userEmail,
                transactionReference: transaction?.reference || '',
                planName: this.selectedPlan?.name || ''
              },
              {
                headers: {
                  'Content-Type': 'application/json'
                }
              }
            ).toPromise();

            if (!result?.success) {
              throw new Error(result?.message || 'Failed to switch plan');
            }

            this.errorMessage = 'Subscription successfully updated! Redirecting to login...';

            // Give user time to read the success message
            await new Promise(resolve => setTimeout(resolve, 2000));

            // Navigate to login with success state
            this.router.navigate(['/login'], {
              state: {
                showRenewalSuccess: true,
                email: this.userEmail
              }
            });
          } catch (error: any) {
            //console.error('Error processing subscription:', error);
            this.errorMessage = error.error?.message ||
              'Payment successful but there was an error updating your subscription. ' +
              'Please contact support with this reference: ' + (transaction?.reference || 'N/A');
            this.isLoading = false;
          }
        },
        onLoad: (response: any) => {
          this.isLoading = false;
        },
        onCancel: () => {
          this.isLoading = false;
        },
        onError: (error: any) => {
          //console.error('Paystack error:', error);
          this.errorMessage = error.message || 'Payment failed. Please try again.';
          this.isLoading = false;
        }
      });

    } catch (error: any) {
      //console.error('Paystack initialization error:', error);
      this.errorMessage = error.message || 'Failed to initialize payment. Please try again.';
      this.isLoading = false;
    }
  }

  /**
   * Activates a new subscription after successful payment
   */
  private async activateNewSubscription(reference: string): Promise<void> {
    try {
      // Use the PaymentService to activate the subscription
      const result = await firstValueFrom(
        this.paymentService.activateSubscription()
      );

      if (!result || !result.success) {
        throw new Error(result?.message || 'Failed to activate subscription');
      }
    } catch (error) {
      //console.error('Error activating subscription:', error);
      throw new Error('Payment was successful but we encountered an error activating your subscription. Please contact support.');
    }
  }


  /**
   * Generate and open the subscription management link
   * Uses our backend endpoint which proxies the request to Paystack's API
   */
  async generateUpdateLink(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      // Call our backend endpoint which will get the subscription code from the tenant's context
      const result = await firstValueFrom(
        this.http.get<any>(`${environment.apiUrl}/api/Subscription/paystack/manage-link`, {
          headers: {
            'Authorization': `Bearer ${this.authService.getToken()}`
          }
        })
      );

      if (result?.status && result.data?.link) {
        // Open the management link in a new tab
        window.open(result.data.link, '_blank');
      } else {
        throw new Error(result?.message || 'Failed to generate management link');
      }
    } catch (error: any) {
      //console.error('Error generating update link:', error);
      this.errorMessage = error.error?.message || 'Failed to generate update link. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Handle payment submission
   */
  async handlePayment(): Promise<void> {
    if (this.isPlanExpired) {
      await this.activatePlanExpiredSubscription();
    } else {
      await this.initializePaystack();
    }
  }

  /**
   * For plan_expired users: create subscription with start_date = CurrentPeriodEnd
   * No upfront charge — first payment happens at the start of the next billing cycle
   */
  private async activatePlanExpiredSubscription(): Promise<void> {
    if (!this.selectedPlan) {
      this.errorMessage = 'Please select a subscription plan.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    try {
      const result = await this.http.post<any>(
        `${environment.apiUrl}/api/Subscription/switch-plan-deferred`,
        {
          email: this.userEmail,
          planCode: this.selectedPlan.planCode,
          planName: this.selectedPlan.name
        },
        { headers: { 'Content-Type': 'application/json' } }
      ).toPromise();

      if (!result?.success) {
        throw new Error(result?.message || 'Failed to switch plan');
      }

      this.errorMessage = 'Plan updated successfully! Redirecting to login...';
      await new Promise(resolve => setTimeout(resolve, 2000));

      this.authService.clearTokens();
      this.renewalSuccess.emit();
      this.router.navigate(['/login'], {
        state: { showRenewalSuccess: true, email: this.userEmail }
      });
    } catch (error: any) {
      this.errorMessage = error.error?.message || 'Failed to update plan. Please try again or contact support.';
      this.isLoading = false;
    }
  }

  logout(): void {
    this.authService.logOut();
    this.router.navigate(['/login']);
  }
}
